import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserRound } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { expenseClaimService } from '@/services/expenseClaimService'
import type { ExpenseClaimLookupItem } from '@/api/types'

/** Collapse "Name Name" duplicates from API (e.g. "Hamid Anjum Hamid Anjum"). */
function formatEmployeePickerLabel(raw: string): string {
  const t = raw.trim().replace(/\s+/g, ' ')
  if (!t) return ''
  const words = t.split(' ')
  if (words.length >= 2 && words.length % 2 === 0) {
    const half = words.length / 2
    const first = words.slice(0, half).join(' ')
    const second = words.slice(half).join(' ')
    if (first === second) return first
  }
  const out: string[] = []
  for (const w of words) {
    if (out[out.length - 1] !== w) out.push(w)
  }
  return out.join(' ')
}

/** Popover content is portaled outside DialogContent; clicks there must not be swallowed as "outside" the dialog. */
function isEventFromPortaledOverlay(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null
  if (!el) return false
  return Boolean(
    el.closest('[data-radix-popper-content-wrapper]') ||
      el.closest('[data-searchable-select-portal]') ||
      el.closest('[data-radix-popover-content]') ||
      el.closest('[cmdk-root]') ||
      el.closest('[cmdk-list]')
  )
}

/** GET /api/ExpenseClaimRequest/GetAllEmployee/{branchId} */
function labelForLookup(e: ExpenseClaimLookupItem): string {
  const raw =
    (e.lookupNameEn ?? e.lookupNameAr ?? e.nameEn ?? e.nameAr ?? e.name ?? '').trim() || ''
  const formatted = formatEmployeePickerLabel(raw)
  return formatted || `ID ${e.id}`
}

export type EmployeeListScopeDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  branchId: number
  onConfirm: (empId: number) => void
  /** When > 0, user is changing employee (dialog may be dismissed without picking). */
  currentEmpId?: number
}

export const EmployeeListScopeDialog: React.FC<EmployeeListScopeDialogProps> = ({
  open,
  onOpenChange,
  branchId,
  onConfirm,
  currentEmpId = 0,
}) => {
  const { t } = useTranslation('common')
  const [employees, setEmployees] = useState<ExpenseClaimLookupItem[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState('')

  useEffect(() => {
    if (!open || !branchId) return
    let cancelled = false
    setLoading(true)
    void expenseClaimService
      .getEmployees(branchId)
      .then((list) => {
        if (!cancelled) setEmployees(list ?? [])
      })
      .catch(() => {
        if (!cancelled) setEmployees([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, branchId])

  useEffect(() => {
    if (open) {
      setSelected(currentEmpId > 0 ? String(currentEmpId) : '')
    }
  }, [open, currentEmpId])

  const options = useMemo(
    () =>
      employees
        .filter((e) => (e.id ?? 0) > 0)
        .map((e) => {
          const label = labelForLookup(e)
          return {
            value: String(e.id),
            label,
            _searchKey: `${label} ${e.id}`.toLowerCase(),
          }
        }),
    [employees]
  )

  const blockDismiss = currentEmpId === 0

  const handleConfirm = () => {
    const id = Number(selected)
    if (!id || Number.isNaN(id)) return
    onConfirm(id)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent
        hideCloseButton={blockDismiss}
        className="gap-0 overflow-visible rounded-2xl border-0 p-0 shadow-2xl ring-1 ring-black/5 dark:ring-white/10 sm:max-w-[440px]"
        onPointerDownOutside={(e) => {
          if (!blockDismiss) return
          if (isEventFromPortaledOverlay(e.target)) return
          e.preventDefault()
        }}
        onInteractOutside={(e) => {
          if (!blockDismiss) return
          if (isEventFromPortaledOverlay(e.target)) return
          e.preventDefault()
        }}
        onEscapeKeyDown={(e) => {
          if (blockDismiss) e.preventDefault()
        }}
      >
        <div className="relative overflow-hidden bg-gradient-to-br from-primary/[0.14] via-primary/[0.06] to-background px-6 pb-5 pt-6">
          <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/[0.1] blur-2xl" />
          <div className="pointer-events-none absolute bottom-0 left-1/3 h-24 w-48 rounded-full bg-primary/[0.06] blur-2xl" />
          <DialogHeader className="relative space-y-0 pr-10 text-left">
            <div className="flex gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary shadow-inner ring-1 ring-primary/15">
                <UserRound className="h-6 w-6" strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1 space-y-2 pt-0.5">
                <DialogTitle className="text-xl font-semibold tracking-tight text-foreground">
                  {t('employeeListScope.dialogTitle', { defaultValue: 'Select employee' })}
                </DialogTitle>
                <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                  {t('employeeListScope.dialogDescription', {
                    defaultValue:
                      'Choose an employee to load their records. You can change this later from the list page.',
                  })}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="border-t border-border/70 bg-background px-6 py-5">
          <SearchableSelect
            label={t('employeeListScope.employeeLabel', { defaultValue: 'Employee' })}
            options={options}
            value={selected}
            onValueChange={setSelected}
            placeholder={t('employeeListScope.selectPlaceholder', { defaultValue: 'Select employee…' })}
            searchPlaceholder={t('employeeListScope.searchPlaceholder', { defaultValue: 'Search by name or code…' })}
            emptyMessage={
              loading
                ? t('employeeListScope.loading', { defaultValue: 'Loading…' })
                : t('employeeListScope.empty', { defaultValue: 'No employees found.' })
            }
            disabled={loading || !branchId}
          />
        </div>

        <DialogFooter className="gap-2 border-t border-border/70 bg-muted/25 px-6 py-4 sm:justify-end">
          {!blockDismiss ? (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('employeeListScope.cancel', { defaultValue: 'Cancel' })}
            </Button>
          ) : null}
          <Button type="button" className="min-w-[100px] font-medium" onClick={handleConfirm} disabled={loading || !selected}>
            {t('employeeListScope.confirm', { defaultValue: 'Continue' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
